import { useState, useEffect } from 'react';
import { Users, UserPlus, Plus, Edit, Trash2, X, Upload } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { API, authFetch, getAuthHeaders } from './helpers';

const TeamTab = () => {
  const [team, setTeam] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', role: '', email: '', phone: '', bio: '', can_login: false, user_role: 'volunteer', order: 0, image_url: '' });
  const [uploading, setUploading] = useState(false);

  const fetchTeam = async () => {
    const res = await authFetch(`${API}/team?active_only=false`);
    if (res.ok) setTeam(await res.json());
  };

  useEffect(() => { fetchTeam(); }, []);

  const openNew = () => { setEditing(null); setForm({ name: '', role: '', email: '', phone: '', bio: '', can_login: false, user_role: 'volunteer', order: 0, image_url: '' }); setShowForm(true); };

  const openEdit = (member) => {
    setEditing(member);
    setForm({ name: member.name || '', role: member.role || '', email: member.email || '', phone: member.phone || '', bio: member.bio || '', can_login: member.can_login || false, user_role: member.user_role || 'volunteer', order: member.order || 0, image_url: member.image_url || '' });
    setShowForm(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await fetch(`${API}/upload`, { method: 'POST', headers: getAuthHeaders(), body: fd });
      if (res.ok) {
        const data = await res.json();
        setForm(prev => ({ ...prev, image_url: data.url }));
      }
    } catch {}
    setUploading(false);
  };

  const saveMember = async (e) => {
    e.preventDefault();
    if (editing) {
      await authFetch(`${API}/team/${editing.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
    } else {
      await authFetch(`${API}/team`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
    }
    setShowForm(false); setEditing(null); setForm({ name: '', role: '', email: '', phone: '', bio: '', can_login: false, user_role: 'volunteer', order: 0 }); fetchTeam();
  };

  const toggleActive = async (id, current) => {
    await authFetch(`${API}/team/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ is_active: !current }) });
    fetchTeam();
  };

  const updateLogin = async (id, canLogin, userRole) => {
    await authFetch(`${API}/team/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ can_login: canLogin, user_role: userRole }) });
    fetchTeam();
  };

  const deleteMember = async (id) => {
    if (!window.confirm('Are you sure you want to delete this team member?')) return;
    await authFetch(`${API}/team/${id}`, { method: 'DELETE' });
    fetchTeam();
  };

  return (
    <div data-testid="team-tab">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-brand-dark">Team & Staff</h2>
        <button onClick={openNew} className="flex items-center gap-2 bg-brand-gold px-4 py-2 text-sm font-medium text-brand-dark hover:opacity-90" data-testid="add-team-member-btn">
          <UserPlus className="w-4 h-4" /> Add Team Member
        </button>
      </div>

      {showForm && (
        <div className="bg-white p-6 mb-6" data-testid="team-member-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-brand-dark">{editing ? 'Edit Team Member' : 'Add New Team Member'}</h3>
            <button onClick={() => { setShowForm(false); setEditing(null); }} className="p-1 text-gray-400 hover:text-brand-dark"><X className="w-5 h-5" /></button>
          </div>
          <form onSubmit={saveMember} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label className="text-brand-dark font-medium">Full Name</Label><Input required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="mt-1" data-testid="member-name-input" /></div>
              <div><Label className="text-brand-dark font-medium">Role / Title</Label><Input required value={form.role} onChange={(e) => setForm({...form, role: e.target.value})} className="mt-1" data-testid="member-role-input" /></div>
              <div><Label className="text-brand-dark font-medium">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="mt-1" data-testid="member-email-input" /></div>
              <div><Label className="text-brand-dark font-medium">Phone</Label><Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="mt-1" /></div>
            </div>
            <div><Label className="text-brand-dark font-medium">Bio</Label><Textarea value={form.bio} onChange={(e) => setForm({...form, bio: e.target.value})} className="mt-1" rows={3} /></div>
            <div>
              <Label className="text-brand-dark font-medium">Profile Photo</Label>
              <div className="mt-1 flex items-center gap-4">
                {form.image_url && <img src={form.image_url} alt="Preview" className="w-12 h-12 rounded-full object-cover border border-gray-200" />}
                <label className="flex items-center gap-2 bg-white border border-gray-300 px-4 py-2 text-sm font-medium text-brand-dark hover:bg-gray-50 cursor-pointer">
                  <Upload className="w-4 h-4" /> {uploading ? 'Uploading...' : 'Upload Photo'}
                  <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" disabled={uploading} data-testid="member-photo-upload" />
                </label>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2 border-t">
              <div className="flex items-center gap-3">
                <input type="checkbox" id="canLogin" checked={form.can_login} onChange={(e) => setForm({...form, can_login: e.target.checked})} className="w-4 h-4" data-testid="member-login-check" />
                <Label htmlFor="canLogin" className="text-brand-dark">Can access dashboard</Label>
              </div>
              <div>
                <Label className="text-brand-dark font-medium">Dashboard Role</Label>
                <select value={form.user_role} onChange={(e) => setForm({...form, user_role: e.target.value})} className="mt-1 w-full border border-gray-300 p-2 rounded" data-testid="member-user-role-select">
                  <option value="volunteer">Volunteer (view only)</option>
                  <option value="staff">Staff (view + manage)</option>
                  <option value="admin">Admin (full access)</option>
                </select>
              </div>
              <div>
                <Label className="text-brand-dark font-medium">Display Order</Label>
                <Input type="number" value={form.order} onChange={(e) => setForm({...form, order: parseInt(e.target.value) || 0})} className="mt-1" />
              </div>
            </div>
            <div className="flex gap-4">
              <button type="submit" className="bg-brand-gold px-6 py-2 text-sm font-medium text-brand-dark hover:opacity-90" data-testid="save-team-member-btn">{editing ? 'Update Member' : 'Add Member'}</button>
              <button type="button" onClick={() => { setShowForm(false); setEditing(null); }} className="bg-gray-200 px-6 py-2 text-sm font-medium text-gray-700 hover:bg-gray-300">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {team.length === 0 ? (
        <div className="text-center py-12 bg-white"><Users className="w-12 h-12 text-gray-300 mx-auto mb-4" /><p className="text-gray-500">No team members yet.</p></div>
      ) : (
        <div className="space-y-3" data-testid="team-list">
          {team.map((member) => (
            <div key={member.id} className={`bg-white p-5 ${!member.is_active ? 'opacity-50' : ''}`} data-testid={`team-member-${member.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  {member.image_url && <img src={member.image_url} alt={member.name} className="w-10 h-10 rounded-full object-cover flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1">
                    <h3 className="font-medium text-brand-dark">{member.name}</h3>
                    <span className="text-sm text-gray-500">{member.role}</span>
                    {member.can_login && <span className={`px-2 py-0.5 text-xs font-medium ${member.user_role === 'admin' ? 'bg-red-100 text-red-700' : member.user_role === 'staff' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}>{member.user_role}</span>}
                    {!member.is_active && <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-500">Inactive</span>}
                  </div>
                  <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                    {member.email && <span>{member.email}</span>}
                    {member.phone && <span>{member.phone}</span>}
                  </div>
                  {member.bio && <p className="text-sm text-gray-500 mt-2 line-clamp-2">{member.bio}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-1 ml-4 flex-shrink-0">
                  <button onClick={() => openEdit(member)} className="p-2 text-gray-400 hover:text-brand-dark" title="Edit" data-testid={`edit-member-${member.id}`}><Edit className="w-4 h-4" /></button>
                  <button onClick={() => toggleActive(member.id, member.is_active)} className={`p-2 ${member.is_active ? 'text-green-500 hover:text-red-600' : 'text-gray-400 hover:text-green-600'}`} title={member.is_active ? 'Deactivate' : 'Activate'}>
                    {member.is_active ? <X className="w-4 h-4" /> : <Users className="w-4 h-4" />}
                  </button>
                  <button onClick={() => deleteMember(member.id)} className="p-2 text-gray-400 hover:text-red-600" title="Delete"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TeamTab;
